// Stempeluhr — ein großer Knopf, mehr braucht es nicht.
//
// Der Zustand ist immer eindeutig ablesbar: läuft die Schicht oder nicht.
// Die laufende Zeit tickt mit, damit man sieht, dass gestempelt IST.
//
// Daneben liegt der Urlaub: beantragen und nachsehen, was daraus geworden ist.
// Beides gehört zusammen — es ist dieselbe Frage ("wann arbeite ich"), nur einmal
// rückwärts und einmal vorwärts.

import { useEffect, useMemo, useState } from "react";
import {
  useLaufendeSchicht,
  useMeineZeiten,
  useEinstempeln,
  useAusstempeln,
  dauerMinuten,
  formatDauer,
  type TimeEntry,
} from "@/lib/zeit";
import { LogoLoader } from "@/components/LogoLoader";
import { Button, Card, Segmented, Sheet, cn } from "@/design-system/ui";
import { usePausenregel, pauseFuer } from "@/lib/pausenregel";
import { ModulHeader } from "@/modules/home/ModulHeader";
import { UrlaubTab } from "./UrlaubTab";

type Register = "uhr" | "urlaub";

export default function ZeitPage() {
  const [register, setRegister] = useState<Register>("uhr");

  return (
    <div className="mx-auto flex h-full max-w-2xl flex-col">
      <ModulHeader titel="Zeiterfassung" />

      <div className="shrink-0 border-b border-ink-700/70 px-4 py-3">
        <Segmented
          value={register}
          onChange={setRegister}
          options={[
            { value: "uhr", label: "Stempeluhr" },
            { value: "urlaub", label: "Urlaub" },
          ]}
        />
      </div>

      {register === "uhr" ? <StempelUhr /> : <UrlaubTab />}
    </div>
  );
}

function StempelUhr() {
  const { data: schicht, isLoading } = useLaufendeSchicht();
  const { data: zeiten = [] } = useMeineZeiten();
  const einstempeln = useEinstempeln();
  const ausstempeln = useAusstempeln();

  const [pauseSheet, setPauseSheet] = useState(false);
  const { data: pausenstufen } = usePausenregel();
  const [fehler, setFehler] = useState<string | null>(null);

  // Sekundengenau mitlaufen lassen — ohne Ticken wirkt die Uhr eingefroren.
  const [jetzt, setJetzt] = useState(Date.now());
  useEffect(() => {
    if (!schicht) return;
    const t = setInterval(() => setJetzt(Date.now()), 1000);
    return () => clearInterval(t);
  }, [schicht]);

  const laufend = schicht ? dauerMinuten(schicht, jetzt) : 0;

  // Anwesend = gelaufene Zeit plus bereits abgezogene Pause. Aus dieser
  // ANWESENHEIT ergibt sich der Abzug — nicht aus der schon gekürzten Zeit,
  // sonst rechnet man die Pause gegen sich selbst.
  const anwesend = schicht ? laufend + schicht.break_minutes : 0;
  const pause = pauseFuer(anwesend, pausenstufen);

  const dieseWoche = useMemo(() => {
    const montag = new Date();
    montag.setHours(0, 0, 0, 0);
    montag.setDate(montag.getDate() - ((montag.getDay() + 6) % 7));
    return zeiten
      .filter((e) => e.ended_at && new Date(e.started_at) >= montag)
      .reduce((sum, e) => sum + dauerMinuten(e), 0);
  }, [zeiten]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LogoLoader size="lg" label="Stempeluhr wird geladen" />
      </div>
    );
  }

  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-6">
        <p className="mb-5 text-center text-sm text-ink-400">
          Diese Woche {formatDauer(dieseWoche)}
        </p>

        {/* Der große Knopf */}
        <div className="mb-8 flex flex-col items-center">
          <div
            className={cn(
              "mb-5 text-center transition-opacity",
              schicht ? "opacity-100" : "opacity-50"
            )}
          >
            <div className="text-5xl font-semibold tabular-nums text-ink-50">
              {schicht ? uhrzeit(laufend) : "0:00"}
            </div>
            <div className="mt-1 text-sm text-ink-400">
              {schicht ? `seit ${zeitVon(schicht.started_at)} Uhr` : "nicht eingestempelt"}
            </div>
          </div>

          <button
            onClick={async () => {
              setFehler(null);
              try {
                if (schicht) setPauseSheet(true);
                else await einstempeln.mutateAsync();
              } catch (e) {
                setFehler((e as Error).message);
              }
            }}
            disabled={einstempeln.isPending}
            className={cn(
              "grid h-44 w-44 place-items-center rounded-full text-lg font-semibold",
              "shadow-xl transition-transform duration-fast active:scale-95",
              "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-offset-4 focus-visible:ring-offset-ink-950",
              schicht
                ? "bg-danger-bg text-white shadow-danger-bg/25 focus-visible:ring-danger-bg/50"
                : "bg-gruen-500 text-gruen-on shadow-gruen-500/25 focus-visible:ring-gruen-500/50"
            )}
          >
            {schicht ? "Ausstempeln" : "Einstempeln"}
          </button>

          {schicht && (
            <p className="mt-4 flex items-center gap-1.5 text-sm text-gruen-400">
              <span className="h-2 w-2 animate-pulse rounded-full bg-gruen-400" />
              Schicht läuft
            </p>
          )}

          {/* Der Stempel liegt auf dem Gerät und ist noch nicht übertragen.
              Das ist KEIN Fehler — die Zeit ist festgeschrieben und geht nicht
              verloren. Aber der Mitarbeiter muss es wissen, sonst tippt er ein
              zweites Mal, weil er glaubt, es habe nicht geklappt. */}
          {schicht?.nochNichtUebertragen && (
            <p className="mt-4 rounded-lg border border-warn-bg/30 bg-warn-bg/10 px-3 py-2 text-sm text-warn-fg">
              Auf dem Gerät gespeichert — wird übertragen, sobald wieder Netz da ist.
              Die Zeit läuft trotzdem.
            </p>
          )}

          {fehler && (
            <p className="mt-4 rounded-lg border border-danger-bg/30 bg-danger-bg/10 px-3 py-2 text-sm text-danger-fg">
              {fehler}
            </p>
          )}
        </div>

        {/* Historie */}
        <section>
          <h2 className="mb-2 text-xs font-medium uppercase tracking-wider text-ink-400">
            Meine Zeiten
          </h2>

          {zeiten.filter((z) => z.ended_at).length === 0 ? (
            <p className="py-6 text-center text-sm text-ink-500">Noch keine erfassten Zeiten.</p>
          ) : (
            <ul className="space-y-2">
              {zeiten
                .filter((z) => z.ended_at)
                .map((z) => (
                  <li key={z.id}>
                    <ZeitZeile z={z} />
                  </li>
                ))}
            </ul>
          )}
        </section>
      </div>

      {/* Ausstempeln: Pause erfassen */}
      {pauseSheet && schicht && (
        <Sheet
          open
          onClose={() => setPauseSheet(false)}
          title="Ausstempeln"
          footer={
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              loading={ausstempeln.isPending}
              onClick={async () => {
                await ausstempeln.mutateAsync({ id: schicht.id, pause });
                setPauseSheet(false);
              }}
            >
              {formatDauer(Math.max(0, laufend + schicht.break_minutes - pause))} buchen
            </Button>
          }
        >
          <div className="space-y-5">
            <div className="rounded-xl border border-ink-700/70 bg-ink-850 p-4 text-center">
              <div className="text-xs uppercase tracking-wider text-ink-400">Anwesend seit</div>
              <div className="mt-1 text-2xl font-semibold tabular-nums text-ink-50">
                {zeitVon(schicht.started_at)} Uhr
              </div>
            </div>

            {/* ══ Die Pause wird gerechnet, nicht getippt ══
                Vorher stand hier ein Stepper. Das war Tipparbeit am Handy — mit
                Handschuhen, im Stehen — und wer die Pause wegdrehte, verschaffte
                sich Arbeitszeit. Jetzt gilt für alle dieselbe Staffel, die die
                Leitung vorgibt. */}
            <div>
              <h3 className="mb-2.5 text-xs font-medium uppercase tracking-wider text-ink-400">
                Pause
              </h3>
              <div className="rounded-xl border border-ink-700/70 bg-ink-850 px-4 py-3 text-center">
                <div className="text-2xl font-semibold tabular-nums text-ink-50">
                  {pause} min
                </div>
                <p className="mt-1 text-sm text-ink-400">
                  {pause > 0
                    ? "wird von der Arbeitszeit abgezogen"
                    : "keine Pause bei dieser Dauer"}
                </p>
              </div>
            </div>
          </div>
        </Sheet>
      )}
    </>
  );
}

function ZeitZeile({ z }: { z: TimeEntry }) {
  const min = dauerMinuten(z);
  return (
    <Card className="flex items-center justify-between gap-3 px-4 py-3">
      <div className="min-w-0">
        <div className="text-[15px] font-medium text-ink-100">
          {new Date(z.started_at).toLocaleDateString("de-DE", {
            weekday: "short",
            day: "2-digit",
            month: "2-digit",
          })}
        </div>
        <div className="mt-0.5 text-sm tabular-nums text-ink-400">
          {zeitVon(z.started_at)} – {z.ended_at ? zeitVon(z.ended_at) : "…"}
          {z.break_minutes > 0 && (
            <span className="text-ink-500"> · {z.break_minutes} min Pause</span>
          )}
        </div>
      </div>
      <div className="shrink-0 text-[15px] font-semibold tabular-nums text-ink-50">
        {formatDauer(min)}
      </div>
    </Card>
  );
}

/** Minuten → "7:42" (für die laufende Uhr) */
function uhrzeit(min: number) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}:${String(m).padStart(2, "0")}`;
}

function zeitVon(iso: string) {
  return new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}
